import React, { useState } from "react";
import { Modal } from "antd";
import { 
  MdUpload, 
  MdDownload, 
  MdRefresh, 
  MdCloudUpload,
  MdClose 
} from "react-icons/md";
import { turnoApi } from "../../services/api/turnoApi";
import * as XLSX from "xlsx";

const ModalCargaMasivaTurnoDetalle = ({ 
  turnoSeleccionado, 
  isOpen, 
  onClose, 
  showToast 
}) => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [uploadMeta, setUploadMeta] = useState(null);
  const [showConfirmParcial, setShowConfirmParcial] = useState(false);

  const mapResultados = (resultados) => ({
    total: resultados.total_procesados,
    success: resultados.nuevos_insertados,
    existing: resultados.existentes || 0,
    errors: resultados.errores,
    details: resultados.detalles_errores || [],
    existingDetails: resultados.detalles_existentes || [],
    puedeCargaParcial: resultados.puede_carga_parcial,
    filasValidasPendientes: resultados.filas_validas_pendientes,
    erroresDocumento: resultados.errores_documento_no_encontrado,
    erroresGuardados: resultados.errores_guardados,
    cargaParcial: resultados.carga_parcial,
    idTurnoCabecera: resultados.id_turno_cabecera,
    turnosIdentificados: resultados.turnos_identificados,
    turnosResumen: resultados.turnos_resumen || [],
    multiTurno: resultados.multi_turno,
  });

  const ejecutarCarga = async (options = {}) => {
    if (!file) {
      showToast("error", "Por favor seleccione un archivo Excel");
      return;
    }

    setIsLoading(true);
    setProgress(0);
    if (!options.keepResults) {
      setResults(null);
      setUploadMeta(null);
    }

    try {
      setProgress(25);

      const response = await turnoApi.bulkUploadTurnoDetalle(
        file,
        turnoSeleccionado?.id || null,
        options
      );

      setProgress(75);

      const resultados = response.data?.resultados;
      if (resultados) {
        const mapped = mapResultados(resultados);
        setResults(mapped);
        setUploadMeta({
          puedeCargaParcial: mapped.puedeCargaParcial,
          idTurnoCabecera: mapped.idTurnoCabecera,
        });
      }

      if (response.data.success === false) {
        showToast(
          "error",
          response.data.message ||
            `Se encontraron ${resultados?.errores || 0} errores en el archivo.`
        );
        setProgress(100);
        return;
      }

      if (resultados) {
        if (resultados.nuevos_insertados > 0) {
          showToast(
            "success",
            `${resultados.nuevos_insertados} registros de turno detalle creados exitosamente`
          );
        }
        if (resultados.existentes > 0) {
          showToast(
            "info",
            resultados.es_reintento
              ? `${resultados.existentes} registros ya estaban cargados (se omitieron)`
              : `${resultados.existentes} registros ya existían en la base de datos`
          );
        }
        if (resultados.errores > 0 && resultados.carga_parcial) {
          const nuevos = resultados.errores_nuevos ?? resultados.errores_guardados ?? resultados.errores;
          const actualizados = resultados.errores_actualizados ?? 0;
          const detalleErrores =
            actualizados > 0
              ? `${nuevos} error(es) nuevos, ${actualizados} actualizado(s)`
              : `${nuevos} error(es) guardados`;
          showToast(
            "warning",
            resultados.es_reintento
              ? `Reintento: ${detalleErrores} para regularización`
              : `${detalleErrores} para regularización`
          );
        } else if (resultados.errores > 0) {
          showToast("warning", `${resultados.errores} registros no se pudieron procesar`);
        }
        if (resultados.total_procesados === 0) {
          showToast("info", "No se encontraron datos válidos para procesar");
        }
      } else {
        showToast("success", response.data.message || "Carga completada exitosamente");
      }

      setProgress(100);
    } catch (error) {
      console.error("❌ Error en carga masiva:", error);
      if (error.response?.data) {
        const errorData = error.response.data;
        showToast(
          "error",
          errorData.message || errorData.detail || errorData.error || "Error en la carga masiva"
        );
      } else {
        showToast("error", "Error de conexión. Verifica tu conexión a internet.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await ejecutarCarga();
  };

  const handleConfirmarCargaParcial = async () => {
    setShowConfirmParcial(false);
    await ejecutarCarga({ forzarCargaParcial: true, keepResults: false });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'xlsm'].includes(fileExtension)) {
        showToast("error", 'Por favor seleccione un archivo Excel válido (.xlsx, .xls o .xlsm)');
        e.target.value = '';
        return;
      }
      setFile(selectedFile);
      setResults(null);
      setUploadMeta(null);
      showToast("info", `Archivo "${selectedFile.name}" seleccionado correctamente`);
    }
  };

  const exportarErroresExcel = () => {
    const details = results?.details || [];
    if (!details.length) {
      showToast("error", "No hay errores para exportar");
      return;
    }

    const filasDetalle = details.map((error) => ({
      "Fila Excel": error.fila ?? "",
      DNI: error.dni || "",
      Comedor: error.comedor || "",
      "Tipo servicio": error.tipo_servicio || "",
      Fecha: error.fecha || "",
      Errores: Array.isArray(error.errores)
        ? error.errores.join(" | ")
        : error.errores || "",
    }));

    const filasResumen = [
      { Campo: "Total filas con error", Valor: details.length },
      {
        Campo: "Generado el",
        Valor: new Date().toLocaleString("es-PE", { timeZone: "America/Lima" }),
      },
      {
        Campo: "Nota",
        Valor:
          "Errores de validación de carga masiva de turno detalle. Corrija el Excel y vuelva a cargar.",
      },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasResumen), "Resumen");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasDetalle), "Errores");
    XLSX.writeFile(wb, `Errores_Carga_Turno_Detalle_${Date.now()}.xlsx`);
    showToast("success", `Excel exportado con ${details.length} error(es)`);
  };

  const downloadTemplate = () => {
    // Crear template con las columnas esperadas
    const templateData = [
      {
        'nombre comedor': 'NOMBRE_COMEDOR_EJEMPLO',
        'tipo servicio': 'TIPO_SERVICIO_EJEMPLO',
        'fecha': '2024-12-31',
        'numero documento comedor': '12345678'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "template_turno_detalle.xlsx");
    
    showToast("success", "Template descargado exitosamente");
  };

  const resetForm = () => {
    setFile(null);
    setResults(null);
    setUploadMeta(null);
    setShowConfirmParcial(false);
    setProgress(0);
    const fileInput = document.querySelector('#excel_file_turno');
    if (fileInput) {
      fileInput.value = '';
    }
    showToast("info", "Formulario reiniciado");
  };

  const handleClose = () => {
    resetForm();
    const shouldReload = results && (results.success > 0 || results.cargaParcial);
    onClose(shouldReload);
  };

  return (
    <>
      {/* Estilos Digital Harmony para Modal Carga Masiva */}
      <style>
        {`
          /* Modal custom styles */
          .modal-carga-masiva .ant-modal-content {
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 15px 35px rgba(0,0,0,0.15);
          }
          
          .modal-carga-masiva .ant-modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-bottom: none;
            padding: 1.5rem 2rem;
          }
          
          .modal-carga-masiva .ant-modal-title {
            color: white;
            font-weight: 700;
            font-size: 1.3rem;
          }
          
          .modal-carga-masiva .ant-modal-close {
            color: white;
            font-size: 18px;
            top: 20px;
            right: 24px;
          }
          
          .modal-carga-masiva .ant-modal-close:hover {
            color: #f0f0f0;
          }
          
          /* Botones con Digital Harmony */
          .btn-dh-primary-modal {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
          
          .btn-dh-primary-modal:hover:not(:disabled) {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning-modal {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(240, 147, 251, 0.3);
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
          
          .btn-dh-warning-modal:hover:not(:disabled) {
            background: linear-gradient(135deg, #f5576c 0%, #e91e63 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(240, 147, 251, 0.4);
            color: white;
          }
          
          .btn-dh-info-modal {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(0, 198, 255, 0.3);
            transition: all 0.3s ease;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
          
          .btn-dh-info-modal:hover:not(:disabled) {
            background: linear-gradient(135deg, #0072ff 0%, #0056cc 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 198, 255, 0.4);
            color: white;
          }
          
          /* Upload section */
          .dh-upload-section-modal {
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            border: 2px dashed #16a34a;
            border-radius: 15px;
            padding: 2rem;
            text-align: center;
            transition: all 0.3s ease;
            margin: 1rem 0;
          }
          
          .dh-upload-section-modal:hover {
            border-color: #15803d;
            background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
            transform: translateY(-2px);
          }
          
          .dh-upload-section-modal.has-file {
            border-color: #059669;
            background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
            box-shadow: 0 8px 25px rgba(16, 185, 129, 0.1);
          }
          
          .upload-icon-modal {
            color: #16a34a;
            margin-bottom: 1rem;
          }
          
          .upload-text-modal {
            color: #374151;
            font-size: 1.1rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }
          
          .upload-subtext-modal {
            color: #6b7280;
            font-size: 0.9rem;
            margin-bottom: 1rem;
          }
          
          .file-selected-display-modal {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
            color: white;
            padding: 12px 20px;
            border-radius: 12px;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin: 0.5rem;
            font-size: 1rem;
            box-shadow: 0 4px 14px rgba(5, 150, 105, 0.3);
            border: 2px solid #10b981;
            transition: all 0.3s ease;
            min-width: 200px;
          }
          
          .file-selected-display-modal:hover {
            background: linear-gradient(135deg, #047857 0%, #065f46 100%);
            transform: translateY(-1px);
            box-shadow: 0 6px 20px rgba(5, 150, 105, 0.4);
          }
          
          /* Progress section */
          .dh-progress-section-modal {
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            border: 2px solid #16a34a;
            border-radius: 15px;
            padding: 1.5rem;
            margin: 1rem 0;
          }
          
          .progress-dh-modal {
            height: 10px;
            border-radius: 10px;
            background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
            overflow: hidden;
            margin: 1rem 0;
          }
          
          .progress-bar-dh-modal {
            background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
            border-radius: 10px;
            transition: width 0.5s ease;
            height: 100%;
          }
          
          /* Results section */
          .dh-results-card-modal {
            background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
            border: 2px solid #16a34a;
            border-radius: 15px;
            margin: 1rem 0;
            overflow: hidden;
            box-shadow: 0 8px 20px rgba(16, 185, 129, 0.1);
          }
          
          .dh-results-header-modal {
            background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
            color: white;
            padding: 1rem 1.5rem;
            border-radius: 15px 15px 0 0;
          }
          
          .dh-results-body-modal {
            padding: 1.5rem;
          }
          
          .dh-stat-card-modal {
            background: white;
            border-radius: 12px;
            padding: 1rem;
            text-align: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
            transition: all 0.3s ease;
            border: 2px solid transparent;
          }
          
          .dh-stat-card-modal:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0,0,0,0.1);
            border-color: #667eea;
          }
          
          .stat-number-modal {
            font-size: 2rem;
            font-weight: 800;
            margin-bottom: 0.25rem;
          }
          
          .stat-label-modal {
            font-size: 0.8rem;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          
          /* Alert info */
          .dh-alert-info-modal {
            background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
            border: 2px solid #3b82f6;
            border-radius: 12px;
            padding: 1rem;
            color: #1e40af;
            margin: 1rem 0;
            font-size: 0.9rem;
          }
          
          .dh-alert-warning-modal {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            border: 2px solid #f59e0b;
            border-radius: 12px;
            padding: 1rem;
            color: #92400e;
            margin: 0.5rem 0;
            font-size: 0.85rem;
          }
        `}
      </style>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MdUpload size={24} />
            Carga Masiva - Turno Detalle
          </div>
        }
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        width={800}
        className="modal-carga-masiva"
        destroyOnClose
      >
        <div style={{ padding: '1rem 0' }}>
          {/* Información del turno seleccionado (opcional) */}
          {turnoSeleccionado ? (
            <div className="dh-alert-info-modal">
              <h6 style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#1e40af' }}>
                <i className="fas fa-info-circle me-2"></i>
                Turno Seleccionado
              </h6>
              <div style={{ fontSize: '0.9rem' }}>
                <strong>Comedor:</strong> {turnoSeleccionado?.comedor?.nombre || 'N/A'}<br/>
                <strong>Tipo Servicio:</strong> {turnoSeleccionado?.tipo_servicio_alimentacion?.descripcion || 'N/A'}<br/>
                <strong>Fecha:</strong> {turnoSeleccionado?.fecha_turno ? (() => {
                  // Evitar problemas de zona horaria parseando manualmente
                  const [year, month, day] = turnoSeleccionado.fecha_turno.split('T')[0].split('-');
                  return `${day}/${month}/${year}`;
                })() : 'N/A'}
              </div>
              <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
                Los datos del Excel deben coincidir con este turno.
              </small>
            </div>
          ) : (
            <div className="dh-alert-info-modal">
              <h6 style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#1e40af' }}>
                <i className="fas fa-magic me-2"></i>
                Modo Automático
              </h6>
              <div style={{ fontSize: '0.9rem' }}>
                El sistema <strong>analiza todo el Excel</strong> e identifica cada turno por la combinación
                <strong> comedor + tipo de servicio + fecha</strong>:
                <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                  <li>Puede haber <strong>varios turnos</strong> en un mismo archivo</li>
                  <li>Si el turno ya existe → se usa ese turno cabecera</li>
                  <li>Si no existe → se crea automáticamente un turno por cada combinación distinta</li>
                  <li>Los errores de carga parcial se guardan <strong>por turno</strong> para regularización</li>
                </ul>
              </div>
            </div>
          )}

          {/* Botones de acción superior */}
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn-dh-info-modal"
              onClick={downloadTemplate}
              title="Descargar plantilla Excel"
            >
              <MdDownload size={18} />
              Descargar Template
            </button>
            <button
              type="button"
              className="btn-dh-warning-modal"
              onClick={resetForm}
              disabled={isLoading}
              title="Limpiar formulario"
            >
              <MdRefresh size={18} />
              Limpiar
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Archivo Excel */}
            <div className={`dh-upload-section-modal ${file ? 'has-file' : ''}`}>
              <MdCloudUpload size={48} className="upload-icon-modal" />
              <input
                type="file"
                id="excel_file_turno"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                required
                disabled={isLoading || file}
                style={{ display: 'none' }}
              />
              {!file ? (
                <>
                  <div className="upload-text-modal">Seleccione su archivo Excel</div>
                  <div className="upload-subtext-modal">Formatos soportados: .xlsx, .xls</div>
                  <label 
                    htmlFor="excel_file_turno" 
                    className="btn-dh-primary-modal" 
                    style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
                  >
                    <MdUpload size={18} />
                    Examinar Archivos
                  </label>
                </>
              ) : (
                <>
                  <div className="upload-text-modal">Archivo seleccionado correctamente</div>
                  <div className="upload-subtext-modal">
                    <strong>Archivo cargado:</strong>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div className="file-selected-display-modal">
                      <i className="fas fa-file-excel me-2"></i>
                      {file.name}
                    </div>
                    <button
                      type="button"
                      className="btn-dh-warning-modal"
                      onClick={() => {
                        setFile(null);
                        setResults(null);
                        const fileInput = document.querySelector('#excel_file_turno');
                        if (fileInput) {
                          fileInput.value = '';
                        }
                      }}
                      disabled={isLoading}
                    >
                      <MdClose size={18} />
                      Cambiar Archivo
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Instrucciones */}
            <div className="dh-alert-info-modal">
              <h6 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                <i className="fas fa-info-circle me-2"></i>
                Formato del archivo Excel
              </h6>
              <p style={{ marginBottom: '0.5rem' }}>El archivo debe tener las siguientes columnas en este orden exacto:</p>
              <ol style={{ marginBottom: '0.5rem', paddingLeft: '1.2rem' }}>
                <li><strong>nombre comedor</strong> <span style={{color: '#16a34a'}}>(debe existir en el sistema)</span></li>
                <li><strong>tipo servicio</strong> <span style={{color: '#16a34a'}}>(debe existir en el sistema)</span></li>
                <li><strong>fecha</strong> <span style={{color: '#3b82f6'}}>(formato: YYYY-MM-DD o DD/MM/YYYY)</span></li>
                <li><strong>numero documento comedor</strong> <span style={{color: '#dc2626'}}>(debe existir y ser válido; DNI numérico se completa a 8 dígitos con ceros a la izquierda)</span></li>
              </ol>
              <div className="dh-alert-warning-modal">
                <small>
                  <strong>Importante:</strong> Todas las columnas son obligatorias. 
                  {turnoSeleccionado ? (
                    <span> Los datos del comedor, tipo de servicio y fecha deben coincidir con el turno seleccionado.</span>
                  ) : (
                    <span> El sistema puede procesar <strong>varios turnos</strong> en el mismo archivo (una fila por comensal; comedor, servicio y fecha definen el turno).</span>
                  )}
                  {' '}Los números de documento deben corresponder a comensales existentes.
                  Use el template para asegurar el formato correcto.
                </small>
              </div>
            </div>

            {/* Botón de envío */}
            <button
              type="submit"
              className="btn-dh-primary-modal"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '1.1rem',
                marginTop: '1rem',
                opacity: (isLoading || !file) ? 0.6 : 1,
                cursor: (isLoading || !file) ? 'not-allowed' : 'pointer'
              }}
              disabled={isLoading || !file}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Procesando archivo...
                </>
              ) : (
                <>
                  <MdUpload size={20} />
                  Cargar Datos
                </>
              )}
            </button>
          </form>

          {/* Barra de progreso */}
          {isLoading && (
            <div className="dh-progress-section-modal">
              <h6 style={{ fontWeight: 'bold', textAlign: 'center', marginBottom: '0.5rem' }}>
                <i className="fas fa-cog fa-spin me-2"></i>
                Procesando archivo...
              </h6>
              <div className="progress-dh-modal">
                <div
                  className="progress-bar-dh-modal"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>
                <span style={{ fontWeight: 'bold' }}>{progress}% completado</span>
              </div>
            </div>
          )}

          {/* Resultados */}
          {results && (
            <div className="dh-results-card-modal">
              <div className="dh-results-header-modal">
                <h6 style={{ margin: 0, fontWeight: 'bold' }}>
                  <i className="fas fa-chart-bar me-2"></i>
                  Resultados de la Carga
                </h6>
              </div>
              <div className="dh-results-body-modal">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                  <div className="dh-stat-card-modal">
                    <div className="stat-number-modal" style={{ color: '#3b82f6' }}>{results.total}</div>
                    <div className="stat-label-modal">Total Procesados</div>
                  </div>
                  <div className="dh-stat-card-modal">
                    <div className="stat-number-modal" style={{ color: '#10b981' }}>{results.success}</div>
                    <div className="stat-label-modal">Nuevos Insertados</div>
                  </div>
                  <div className="dh-stat-card-modal">
                    <div className="stat-number-modal" style={{ color: '#06b6d4' }}>{results.existing}</div>
                    <div className="stat-label-modal">Ya Existentes</div>
                  </div>
                  <div className="dh-stat-card-modal">
                    <div className="stat-number-modal" style={{ color: '#ef4444' }}>{results.errors}</div>
                    <div className="stat-label-modal">Errores</div>
                  </div>
                </div>

                {/* Turnos identificados en el Excel */}
                {results.turnosResumen && results.turnosResumen.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
                      border: '2px solid #8b5cf6',
                      borderRadius: '12px',
                      padding: '1rem'
                    }}>
                      <h6 style={{ fontWeight: 'bold', color: '#5b21b6', marginBottom: '0.5rem' }}>
                        <i className="fas fa-layer-group me-2"></i>
                        Turnos identificados en el Excel ({results.turnosIdentificados || results.turnosResumen.length})
                      </h6>
                      <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #c4b5fd' }}>
                              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Comedor</th>
                              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Servicio</th>
                              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Fecha</th>
                              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.turnosResumen.map((t, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #ede9fe' }}>
                                <td style={{ padding: '0.5rem' }}>{t.comedor || '—'}</td>
                                <td style={{ padding: '0.5rem' }}>{t.tipo_servicio || '—'}</td>
                                <td style={{ padding: '0.5rem' }}>{t.fecha || '—'}</td>
                                <td style={{ padding: '0.5rem' }}>
                                  {t.estado === 'creado' && (
                                    <span style={{ color: '#059669', fontWeight: 600 }}>Creado</span>
                                  )}
                                  {t.estado === 'existente' && (
                                    <span style={{ color: '#2563eb', fontWeight: 600 }}>Existente</span>
                                  )}
                                  {t.estado === 'error' && (
                                    <span style={{ color: '#dc2626', fontWeight: 600 }} title={t.mensaje}>
                                      Error
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Detalles de errores */}
                {results.details && results.details.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                      border: '2px solid #f87171',
                      borderRadius: '12px',
                      padding: '1rem'
                    }}>
                      <h6 style={{ fontWeight: 'bold', color: '#dc2626', marginBottom: '0.5rem' }}>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Errores Encontrados ({results.details.length} filas con problemas)
                      </h6>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <button
                          type="button"
                          className="btn-dh-info-modal"
                          onClick={exportarErroresExcel}
                          title="Descargar errores en Excel"
                        >
                          <MdDownload size={18} />
                          Exportar errores Excel
                        </button>
                      </div>
                      <div style={{
                        background: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        marginBottom: '0.75rem',
                        fontSize: '0.85rem',
                        color: '#92400e'
                      }}>
                        <strong>⚠️ Importante:</strong> {results.cargaParcial ? (
                          <span>
                            Carga parcial realizada. Los registros válidos fueron insertados y los errores
                            quedaron guardados para regularización posterior desde la pantalla de Gestionar Turnos.
                          </span>
                        ) : results.success > 0 ? (
                          <span>Se encontraron errores de acceso duplicado en algunas filas. Los <strong>{results.success} registros válidos fueron registrados exitosamente</strong>, pero los {results.errors} indicados abajo fueron ignorados. Corrija los errores e intente cargar únicamente las filas corregidas.</span>
                        ) : (
                          <span>Se encontraron errores en el archivo. <strong>NO se insertó ningún registro nuevo.</strong> Puede realizar una carga parcial guardando los errores para regularización.</span>
                        )}
                      </div>
                      <div style={{ 
                        maxHeight: '300px', 
                        overflowY: 'auto',
                        background: 'white',
                        borderRadius: '8px',
                        padding: '0.5rem'
                      }}>
                        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ 
                              borderBottom: '2px solid #e5e7eb',
                              background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)'
                            }}>
                              <th style={{ 
                                padding: '0.75rem', 
                                textAlign: 'left', 
                                color: '#dc2626',
                                fontWeight: 'bold',
                                width: '80px'
                              }}>Fila Excel</th>
                              <th style={{ 
                                padding: '0.75rem', 
                                textAlign: 'left', 
                                color: '#dc2626',
                                fontWeight: 'bold',
                                width: '120px'
                              }}>DNI</th>
                              <th style={{ 
                                padding: '0.75rem', 
                                textAlign: 'left', 
                                color: '#dc2626',
                                fontWeight: 'bold',
                                width: '120px'
                              }}>Turno</th>
                              <th style={{ 
                                padding: '0.75rem', 
                                textAlign: 'left', 
                                color: '#dc2626',
                                fontWeight: 'bold'
                              }}>Error(es)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.details.map((error, index) => (
                              <tr key={index} style={{ 
                                borderBottom: '1px solid #f3f4f6',
                                background: index % 2 === 0 ? '#ffffff' : '#fafafa'
                              }}>
                                <td style={{ 
                                  padding: '0.75rem', 
                                  fontWeight: 'bold',
                                  color: '#dc2626',
                                  fontSize: '0.9rem'
                                }}>
                                  Fila {error.fila}
                                </td>
                                <td style={{ 
                                  padding: '0.75rem',
                                  fontFamily: 'monospace',
                                  color: '#374151',
                                  fontSize: '0.9rem'
                                }}>
                                  {error.dni || 'N/A'}
                                </td>
                                <td style={{ 
                                  padding: '0.75rem',
                                  fontSize: '0.8rem',
                                  color: '#374151'
                                }}>
                                  {[error.comedor, error.tipo_servicio, error.fecha]
                                    .filter(Boolean)
                                    .join(' / ') || '—'}
                                </td>
                                <td style={{ 
                                  padding: '0.75rem',
                                  color: '#6b7280'
                                }}>
                                  {Array.isArray(error.errores) ? (
                                    <ul style={{ 
                                      margin: 0, 
                                      paddingLeft: '1.2rem',
                                      listStyleType: 'disc'
                                    }}>
                                      {error.errores.map((err, idx) => (
                                        <li key={idx} style={{ marginBottom: '0.25rem' }}>
                                          {err}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    error.errores
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {results.details.length > 10 && (
                        <div style={{ 
                          textAlign: 'center', 
                          marginTop: '0.75rem', 
                          padding: '0.5rem',
                          background: '#fef3c7',
                          borderRadius: '8px',
                          fontSize: '0.85rem', 
                          color: '#92400e',
                          fontWeight: '600'
                        }}>
                          <i className="fas fa-info-circle me-2"></i>
                          Mostrando todos los {results.details.length} errores encontrados
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Acciones ante errores */}
                {!results.cargaParcial && results.details?.length > 0 && (
                  <div style={{ marginTop: '1rem', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(results.puedeCargaParcial || uploadMeta?.puedeCargaParcial) && (
                      <button
                        type="button"
                        className="btn-dh-warning-modal"
                        onClick={() => setShowConfirmParcial(true)}
                        disabled={isLoading}
                        title="Cargar registros válidos y guardar errores para regularización"
                      >
                        <i className="fas fa-exclamation-circle me-1" />
                        Cargar con errores (guardar pendientes)
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title="Confirmar carga incompleta"
        open={showConfirmParcial}
        onCancel={() => setShowConfirmParcial(false)}
        onOk={handleConfirmarCargaParcial}
        okText="Sí, cargar incompleto"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
        confirmLoading={isLoading}
      >
        <p>
          Se cargará el archivo <strong>de forma incompleta</strong>: solo se insertarán las filas válidas
          ({results?.filasValidasPendientes ?? 0} pendientes) y los errores quedarán guardados en el sistema.
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Es su responsabilidad regularizar los errores posteriormente</strong> desde la pantalla
          Gestionar Turnos, usando el botón &quot;Ver errores&quot; del turno correspondiente.
        </p>
      </Modal>
    </>
  );
};

export default ModalCargaMasivaTurnoDetalle;